import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { api, loadBuildingsForSelector } from '../../../api';
import { useAuth } from '../../../contexts/AuthContext';
import {
  applyDefaultBuildingSelection,
  needsBuildingSelection,
  shouldShowBuildingSelector,
  selectBuilding,
} from '../../../utils/buildingSelection';
import {
  ActivityLedgerFilters,
  ActivityLedgerRow,
  IeCategory,
  incomeExpenseApi,
} from '../../../api/incomeExpenseApi';
import { printVoucherHtml } from '../../../utils/voucherPrint';
import { exportActivityLedgerReport } from '../activityLedgerExport';
import { getCalendarMonthInputRange } from '../../../utils/apartmentDate';
import { isExcludedFromActivityRevenueCategory } from '../../../utils/ieRevenueActivity.util';

const ACTIVITY_LEDGER_PAGE_SIZE = 25;

const formatCurrency = (n: number) =>
  `${new Intl.NumberFormat('vi-VN').format(Math.round(n))}đ`;

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('vi-VN');

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString('vi-VN');

const ONLINE_SURPLUS_LABEL = 'Nhận tiền từ chuyển khoản online';

const LedgerVoucherModal: React.FC<{
  entryId: string;
  title: string;
  onClose: () => void;
}> = ({ entryId, title, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setFailed(false);
      setHtmlContent('');
      try {
        const html = await incomeExpenseApi.fetchLedgerVoucherHtml(entryId);
        if (!cancelled) setHtmlContent(html);
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [entryId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => htmlContent && printVoucherHtml(htmlContent)}
              disabled={!htmlContent}
              className="text-sm text-cyan-600 hover:text-cyan-700 disabled:text-gray-400"
            >
              In
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-xl leading-none px-2"
              aria-label="Đóng"
            >
              ×
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-gray-100 relative" style={{ minHeight: '70vh' }}>
          {loading && !failed && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />
              <span className="ml-2 text-gray-600 text-sm">Đang tải...</span>
            </div>
          )}
          {failed ? (
            <div className="flex items-center justify-center py-16 text-gray-500 text-sm">
              Không thể tải phiếu
            </div>
          ) : htmlContent ? (
            <iframe
              title={title}
              srcDoc={htmlContent}
              className="w-full border-0 bg-white"
              style={{ minHeight: '70vh', display: 'block' }}
              sandbox="allow-same-origin allow-scripts"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

interface ActivityLedgerTabProps {
  kind: 'revenue' | 'expenditure';
  refreshToken?: number;
  onBuildingIdChange?: (buildingId: string) => void;
  allowEditManualVoucher?: boolean;
  allowCancelManualVoucher?: boolean;
  onEditManualVoucher?: (row: ActivityLedgerRow) => void;
  onCancelManualVoucher?: (row: ActivityLedgerRow) => void;
}

export interface ActivityLedgerTabHandle {
  exportReport: () => Promise<void>;
}

export const ActivityLedgerTab = forwardRef<ActivityLedgerTabHandle, ActivityLedgerTabProps>(function ActivityLedgerTab(
  {
    kind,
    refreshToken = 0,
    onBuildingIdChange,
    allowEditManualVoucher,
    allowCancelManualVoucher,
    onEditManualVoucher,
    onCancelManualVoucher,
  },
  ref,
) {
  const { user } = useAuth();
  const [buildings, setBuildings] = useState<Array<{ id: string; name: string }>>([]);
  const [buildingId, setBuildingId] = useState('');
  const [categories, setCategories] = useState<IeCategory[]>([]);
  const [rows, setRows] = useState<ActivityLedgerRow[]>([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalCashAmount, setTotalCashAmount] = useState(0);
  const [totalTransferAmount, setTotalTransferAmount] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [voucherPreview, setVoucherPreview] = useState<{ entryId: string; title: string } | null>(null);
  const [filters, setFilters] = useState<ActivityLedgerFilters>(() => {
    const { dateFrom, dateTo } = getCalendarMonthInputRange();
    return {
      apartmentSearch: '',
      voucherSearch: '',
      categoryId: '',
      dateFrom,
      dateTo,
    };
  });

  const isRevenue = kind === 'revenue';
  const categoryLabel = isRevenue ? 'Loại phiếu thu' : 'Loại phiếu chi';
  const addressColumn = isRevenue ? 'Căn hộ thu' : 'Căn hộ chi';
  const actualDateColumn = isRevenue ? 'Ngày thực thu' : 'Ngày thực chi';
  const totalLabel = isRevenue ? 'Tổng thu' : 'Tổng chi';

  const listApi = useMemo(
    () =>
      isRevenue
        ? incomeExpenseApi.listRevenueActivityLedger
        : incomeExpenseApi.listExpenditureActivityLedger,
    [isRevenue],
  );

  const listCategories = useMemo(
    () =>
      isRevenue
        ? incomeExpenseApi.listRevenueCategories
        : incomeExpenseApi.listExpenditureCategories,
    [isRevenue],
  );

  useEffect(() => {
    loadBuildingsForSelector().then((data) => {
      const list = data || [];
      setBuildings(list);
      setBuildingId((prev) => applyDefaultBuildingSelection(list, user, prev));
    });
  }, [user]);

  useEffect(() => {
    if (!buildingId) return;
    listCategories(buildingId)
      .then((rows) =>
        setCategories(
          isRevenue
            ? rows.filter((cat) => !isExcludedFromActivityRevenueCategory(cat.code))
            : rows,
        ),
      )
      .catch(() => setCategories([]));
  }, [buildingId, listCategories, isRevenue]);

  useEffect(() => {
    if (buildingId) onBuildingIdChange?.(buildingId);
  }, [buildingId, onBuildingIdChange]);

  useEffect(() => {
    setPage(1);
  }, [filters, buildingId, refreshToken]);

  const loadRows = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    try {
      const activeFilters: ActivityLedgerFilters = {};
      if (filters.apartmentSearch?.trim()) activeFilters.apartmentSearch = filters.apartmentSearch.trim();
      if (filters.voucherSearch?.trim()) activeFilters.voucherSearch = filters.voucherSearch.trim();
      if (filters.categoryId) activeFilters.categoryId = filters.categoryId;
      if (filters.dateFrom) activeFilters.dateFrom = filters.dateFrom;
      if (filters.dateTo) activeFilters.dateTo = filters.dateTo;
      if (filters.paymentForm) activeFilters.paymentForm = filters.paymentForm;

      const data = await listApi(buildingId, activeFilters, {
        page,
        limit: ACTIVITY_LEDGER_PAGE_SIZE,
      });
      setRows(data.rows || []);
      setTotalAmount(data.summary?.totalAmount || 0);
      setTotalCashAmount(data.summary?.totalCashAmount ?? 0);
      setTotalTransferAmount(data.summary?.totalTransferAmount ?? 0);
      setRowCount(data.summary?.rowCount ?? 0);
      setTotalPages(data.summary?.totalPages ?? 0);
    } catch {
      toast.error('Không thể tải hoạt động thu chi');
      setRows([]);
      setTotalAmount(0);
      setTotalCashAmount(0);
      setTotalTransferAmount(0);
      setRowCount(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [buildingId, filters, listApi, refreshToken, page]);

  useEffect(() => {
    const timer = setTimeout(loadRows, 300);
    return () => clearTimeout(timer);
  }, [loadRows]);

  const activeCategories = categories.filter((c) => c.isActive);

  const buildActiveFilters = useCallback((): ActivityLedgerFilters => {
    const activeFilters: ActivityLedgerFilters = {};
    if (filters.apartmentSearch?.trim()) activeFilters.apartmentSearch = filters.apartmentSearch.trim();
    if (filters.voucherSearch?.trim()) activeFilters.voucherSearch = filters.voucherSearch.trim();
    if (filters.categoryId) activeFilters.categoryId = filters.categoryId;
    if (filters.dateFrom) activeFilters.dateFrom = filters.dateFrom;
    if (filters.dateTo) activeFilters.dateTo = filters.dateTo;
    if (filters.paymentForm) activeFilters.paymentForm = filters.paymentForm;
    return activeFilters;
  }, [filters]);

  const exportReport = useCallback(async () => {
    if (!buildingId) {
      toast.error('Vui lòng chọn chung cư trước khi xuất báo cáo');
      return;
    }
    try {
      const activeFilters = buildActiveFilters();
      const data = await listApi(buildingId, activeFilters, { allRows: true });
      const exportRows = data.rows || [];
      if (exportRows.length === 0) {
        toast.error('Không có dữ liệu để xuất báo cáo');
        return;
      }
      const buildingName = buildings.find((b) => b.id === buildingId)?.name || 'chung-cu';
      exportActivityLedgerReport({
        kind,
        buildingName,
        filters: activeFilters,
        rows: exportRows,
        totalAmount: data.summary?.totalAmount ?? 0,
        totalCashAmount: data.summary?.totalCashAmount,
        totalTransferAmount: data.summary?.totalTransferAmount,
      });
      toast.success('Đã xuất báo cáo');
    } catch {
      toast.error('Không thể xuất báo cáo');
    }
  }, [buildingId, buildings, buildActiveFilters, kind, listApi]);

  useImperativeHandle(ref, () => ({ exportReport }), [exportReport]);

  const pageStart = rowCount === 0 ? 0 : (page - 1) * ACTIVITY_LEDGER_PAGE_SIZE + 1;
  const pageEnd = rowCount === 0 ? 0 : Math.min(page * ACTIVITY_LEDGER_PAGE_SIZE, rowCount);

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {shouldShowBuildingSelector(buildings) && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Chung cư</label>
              <select
                value={buildingId}
                onChange={(e) => selectBuilding(user?.id, e.target.value, setBuildingId)}
                disabled={!needsBuildingSelection(buildings)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              >
                {buildings.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs text-gray-500 mb-1">{categoryLabel}</label>
            <select
              value={filters.categoryId || ''}
              onChange={(e) => setFilters((prev) => ({ ...prev, categoryId: e.target.value }))}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            >
              <option value="">Tất cả</option>
              {activeCategories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Căn hộ</label>
            <input
              type="text"
              value={filters.apartmentSearch || ''}
              onChange={(e) => setFilters((prev) => ({ ...prev, apartmentSearch: e.target.value }))}
              placeholder="Mã / tên căn hộ"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Tìm phiếu / mô tả</label>
            <input
              type="text"
              value={filters.voucherSearch || ''}
              onChange={(e) => setFilters((prev) => ({ ...prev, voucherSearch: e.target.value }))}
              placeholder="Số phiếu thu, mô tả..."
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Từ ngày</label>
            <input
              type="date"
              value={filters.dateFrom || ''}
              onChange={(e) => setFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Đến ngày</label>
            <input
              type="date"
              value={filters.dateTo || ''}
              onChange={(e) => setFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Hình thức</label>
            <select
              value={filters.paymentForm || ''}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  paymentForm: (e.target.value as 'CASH' | 'TRANSFER') || undefined,
                }))
              }
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            >
              <option value="">Tất cả</option>
              <option value="CASH">Tiền mặt</option>
              <option value="TRANSFER">Chuyển khoản</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-5 text-center">
          <div className="text-2xl font-bold text-gray-900">{formatCurrency(totalAmount)}</div>
          <div className="text-sm text-gray-500 mt-1">{totalLabel}</div>
          <div className="text-xs text-gray-500 mt-2">
            Tiền mặt: {formatCurrency(totalCashAmount)} · CK: {formatCurrency(totalTransferAmount)}
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-5 text-center">
          <div className="text-2xl font-bold text-gray-900">{rowCount}</div>
          <div className="text-sm text-gray-500 mt-1">Số dòng hoạt động</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-5 text-center">
          <div className="text-sm text-gray-600 mt-2">
            Dữ liệu từ sổ thu chi (phiếu thu dư, cấn trừ, import thủ công)
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">{addressColumn}</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">{categoryLabel}</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Tên phiếu</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Lý do</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Số bút toán</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">{actualDateColumn}</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Ngày tạo</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Người tạo</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Hình thức thanh toán</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase">Số tiền</th>
                <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 uppercase">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={11} className="px-4 py-10 text-center text-gray-500">Đang tải...</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-10 text-center text-gray-500">
                    Chưa có hoạt động {isRevenue ? 'thu' : 'chi'} nào
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{row.apartmentCode || '—'}</div>
                      {row.apartmentName && (
                        <div className="text-xs text-gray-500">{row.apartmentName}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{row.categoryName}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {row.voucherName || row.voucherCode || '—'}
                    </td>
                    <td
                      className="px-3 py-2 min-w-[160px] max-w-[280px] whitespace-normal break-words align-top text-gray-700"
                      title={row.reason || undefined}
                    >
                      {row.reason || '—'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {row.voucherCode || '—'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDate(row.actualDate)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(row.createdAt)}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                      {row.performerName || '—'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                      {row.paymentMethodLabel || '—'}
                    </td>
                    <td className={`px-3 py-2 text-right whitespace-nowrap font-medium ${isRevenue ? 'text-blue-600' : 'text-red-600'}`}>
                      {formatCurrency(row.amount)}
                    </td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      <div className="flex flex-col items-center gap-1">
                        {row.canViewVoucher && row.voucherViewEntryId ? (
                          <button
                            type="button"
                            onClick={() =>
                              setVoucherPreview({
                                entryId: row.voucherViewEntryId!,
                                title:
                                  row.entryType === 'MANUAL_REVENUE' || row.entryType === 'MANUAL_EXPENDITURE'
                                    ? row.reason || row.voucherName
                                    : row.voucherName === ONLINE_SURPLUS_LABEL
                                      ? ONLINE_SURPLUS_LABEL
                                      : row.entryType === 'SURPLUS_OFFSET'
                                        ? 'Cấn trừ phiếu thu'
                                        : 'Đóng dư phiếu thu',
                              })
                            }
                            className="text-cyan-600 hover:text-cyan-800 text-sm font-medium"
                          >
                            Xem
                          </button>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                        {allowEditManualVoucher && row.canEditManualVoucher && onEditManualVoucher && (
                          <button
                            type="button"
                            onClick={() => onEditManualVoucher(row)}
                            className="text-xs text-gray-700 hover:text-gray-900"
                          >
                            Chỉnh sửa
                          </button>
                        )}
                        {allowCancelManualVoucher && row.canCancelManualVoucher && onCancelManualVoucher && (
                          <button
                            type="button"
                            onClick={() => onCancelManualVoucher(row)}
                            className="text-xs text-red-600 hover:text-red-800"
                          >
                            Huỷ
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 text-sm text-gray-500 space-y-3">
          <div>Tổng {rowCount} dòng</div>
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-gray-700">
              <div>
                Đang xem {pageStart} đến {pageEnd} trong tổng số {rowCount} bản ghi
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page === 1 || loading}
                  className="px-3 py-1 bg-white border border-gray-200 rounded disabled:opacity-50"
                >
                  ←
                </button>
                <span className="text-xs text-gray-600">
                  Trang {page} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={page >= totalPages || loading}
                  className="px-3 py-1 bg-white border border-gray-200 rounded disabled:opacity-50"
                >
                  →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {voucherPreview && (
        <LedgerVoucherModal
          entryId={voucherPreview.entryId}
          title={voucherPreview.title}
          onClose={() => setVoucherPreview(null)}
        />
      )}
    </div>
  );
});

export default ActivityLedgerTab;
